import { computed, inject } from 'vue'
import { isCondition, parseCondition } from '@shared/utils/json-logic'
import { useCellRenderers } from '@gu-datagrid-new/composables/CellRenderers'

const { cellRenderers } = useCellRenderers()

const pageVarsQuery = inject('pageVarsQuery')

const user = computed(() => pageVarsQuery?.pageVars?.value?.user || window.siteVars?.user || {})

export function useGridOptions (siteConfig = {}, config = {}) {
  let enableMultirowSelect = siteConfig.value?.data?.enableMultirowSelect ?? config.enableMultirowSelect ?? false
  if (enableMultirowSelect && isCondition(enableMultirowSelect)) {
    enableMultirowSelect = parseCondition(enableMultirowSelect, { user: user.value })
  }

  const columnDefs = computed(() => {
    let baseColumnDefs = siteConfig.value?.data?.columnDefs || config.columnDefs || config.gridOptions?.columnDefs || []
    baseColumnDefs = baseColumnDefs.map(colDef => {
      if (colDef.cellRenderer) {
        // Component refs (imported .vue defaults) are passed straight through
        // to ag-grid; only string names need to be looked up in the registry.
        if (typeof colDef.cellRenderer !== 'string') return colDef
        const rendererName = colDef.cellRenderer
        if (cellRenderers[rendererName]) {
          return {
            ...colDef,
            cellRenderer: cellRenderers[rendererName],
          }
        } else {
          console.warn(`Cell renderer ${rendererName} not found for column ${colDef.headerName || colDef.field}`)
        }
      }
      return colDef
    })

    return baseColumnDefs
  })

  const gridOptions = computed(() => {
    const initialGridOptions = {
      columnDefs: columnDefs.value,
      pagination: true,
      paginationPageSize: 20,
      overlayNoRowsTemplate: `
        <div class="ag-overlay-no-rows-center" style="width: 100%; padding: 12px;">
          ${ siteConfig.value?.data?.apiSettings?.noRowsMessage ?? 'No Data Available' }
        </div>
      `,
      suppressNoRowsOverlay: false,
      suppressContextMenu: true,
      reactiveCustomComponents: true,
      rowBuffer: 0,
      maxBlocksInCache: 1,
      debug: true,
      defaultColDef: {
        filterParams: {
          maxNumConditions: 1,
        },
        resizable: true,
      },
      autoSizeStrategy: {
        type: 'fitCellContents',
      },
      onFirstDataRendered: (params) => {
        const allColumns = params.api.getColumns()
        const columnsToSize = allColumns
          .filter(col => {
            const colDef = col.getColDef()
            const isCheckboxColumn = colDef.pinned === 'left' && colDef.resizable === false && colDef.sortable === false

            return !isCheckboxColumn
          })
          .map(col => col.getColId())

        params.api.autoSizeColumns(columnsToSize, false)

        const checkboxColumn = allColumns.find(col => {
          const colDef = col.getColDef()
          return colDef.pinned === 'left' && colDef.resizable === false && colDef.sortable === false
        })
        if (checkboxColumn) {
          params.api.setColumnWidth(checkboxColumn, 48)
        }
      },
      ...(config.gridOptions || {}),
      ...(siteConfig.value?.gridOptions || {}),
    }

    if (enableMultirowSelect) {
      initialGridOptions['rowSelection'] = {
        mode: 'multiRow',
        checkboxes: true,
        headerCheckbox: false,
        enableClickSelection: false,
      }
      initialGridOptions['selectionColumnDef'] = {
        sortable: false,
        resizable: false,
        maxWidth: 48,
        suppressHeaderMenuButton: true,
        pinned: 'left',
      }
    }

    return initialGridOptions
  })

  return {
    columnDefs,
    gridOptions,
  }
}
